import { NgClass } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, RouterLinkWithHref } from "@angular/router";
import { AccountState, AccountType, Marketplace } from "@front/m19-api-client";
import { AccountGroup, AccountMarketplaceEx, OrganizationAccountGroups } from "@front/m19-models";
import {
  AuthService,
  isReadOnlyAccess,
  OrganizationAccountGroupService,
  OrganizationUsersService,
} from "@front/m19-services";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  IBadgeComponent,
  IButtonDirective,
  ICheckboxComponent,
  IDataTableComponent,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuSeparatorComponent,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  ITableDataSource,
  ITableImports,
  ITablePagination,
  ITableTextFilterConfig,
  ITooltipDirective,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { CatalogToolbarComponent } from "@m19-board/catalog/catalog-sections/catalog-page/catalog-toolbar/catalog-toolbar.component";
import { NavBreadcrumbDirective } from "@m19-board/containers/page-layout/nav-breadcrumb.directive";
import { AccountBidderStatus, AccountSettingsV2Service } from "@m19-board/services/account-settings.service";
import { BreadcrumbItemComponent } from "@m19-board/shared/breadcrumb-item/breadcrumb-item.component";
import {
  CompactFilterComponent,
  Filter,
  FilterCategory,
} from "@m19-board/shared/compact-filter/compact-filter.component";
import { CompactFilterService } from "@m19-board/shared/compact-filter/compact-filter.service";
import { BehaviorSubject, combineLatest, map, switchMap, tap } from "rxjs";
import { ModalCreateAccountGroupComponent, ModalCreateAccountGroupData } from "../account-settings.component";
import { AccountListRowComponent } from "./account-list-row.component";

@Component({
  selector: "app-account-list",
  imports: [
    NavBreadcrumbDirective,
    BreadcrumbItemComponent,
    TranslocoDirective,
    IDataTableComponent,
    ITableImports,
    IButtonDirective,
    IBadgeComponent,
    CompactFilterComponent,
    AccountListRowComponent,
    CatalogToolbarComponent,
    IButtonDirective,
    ITooltipDirective,
    IDropdownMenuSeparatorComponent,
    IDropdownMenuComponent,
    IDropdownMenuTriggerDirective,
    IDropdownMenuItemDirective,
    NgClass,
    RouterLinkWithHref,
    ICheckboxComponent,
    IIconComponent,
  ],
  templateUrl: "./account-list.component.html",
  providers: [AccountSettingsV2Service],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountListComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly organizationAccountGroupService = inject(OrganizationAccountGroupService);
  private readonly accountSettingsV2Service = inject(AccountSettingsV2Service);
  private readonly toasterService = inject(ToasterService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly compactFilterService = inject(CompactFilterService);
  private readonly authService = inject(AuthService);
  private readonly organizationUsersService = inject(OrganizationUsersService);

  readonly productTour = toSignal(this.authService.isProductTour$, { initialValue: false });

  readonly DEFAULT_PAGE_SIZE = 25;

  readonly organization$ = combineLatest([
    this.organizationAccountGroupService.allOrganizationAccountGroups$,
    this.route.params,
  ]).pipe(
    map(([orgs, params]) => {
      if (!orgs?.length) return undefined;
      const orgId = Number(params["id"]);
      const org = orgs?.find((o: OrganizationAccountGroups) => o.organization.organizationId === orgId);
      return org;
    }),
  );

  readonly accountMarketplaces$ = this.organization$.pipe(
    map((org) => org?.accountGroups.flatMap((group) => group.resources) ?? []),
  );
  readonly filters$ = new BehaviorSubject<Filter<FilterCategory>[]>([]);
  readonly filteredAccountMarketplaces$ = combineLatest([this.accountMarketplaces$, this.filters$]).pipe(
    map(([accountMarketplaces, filters]) => {
      for (const filter of filters) {
        const hasValues = filter.values?.length > 0;
        if (filter.type === FilterCategory.Marketplace) {
          accountMarketplaces = accountMarketplaces.filter(
            (am) => (!hasValues || filter.values?.some((v): v is Marketplace => v === am.marketplace)) ?? false,
          );
        }
        if (filter.type === FilterCategory.AccountGroup) {
          accountMarketplaces = accountMarketplaces.filter(
            (am) =>
              (!hasValues ||
                filter.values?.some(
                  (v): v is AccountGroup => (v as unknown as AccountGroup).id === am.accountGroupId,
                )) ??
              false,
          );
        }
        if (filter.type === FilterCategory.AccountType) {
          accountMarketplaces = accountMarketplaces.filter(
            (am) => (!hasValues || filter.values?.some((v): v is AccountType => v === am.accountType)) ?? false,
          );
        }
        if (filter.type === FilterCategory.AccountBidderStatus) {
          accountMarketplaces = accountMarketplaces.filter(
            (am) =>
              !hasValues ||
              filter.values?.some(
                (v): v is AccountBidderStatus => v === this.accountSettingsV2Service.getAccountBidderStatus(am),
              ),
          );
        }
      }
      return accountMarketplaces;
    }),
  );

  readonly accountMarketplaces = toSignal(this.accountMarketplaces$);

  readonly isReadOnly = computed(() => {
    return isReadOnlyAccess(this.organization()?.accessLevel);
  });

  readonly adminOrganizationIds = toSignal(this.organizationUsersService.adminOrganizationIds$);
  readonly canEditAccounts = computed(() => {
    const productTour = this.productTour();
    if (productTour) {
      return false;
    }
    const org = this.organization();
    return org && this.adminOrganizationIds()?.has(org.organization.organizationId!);
  });

  readonly byProfileId = toSignal(
    this.accountMarketplaces$.pipe(map((ams) => new Map(ams.map((am) => [am.profileId!, am])))),
  );

  readonly organization = toSignal(this.organization$);

  readonly selectedProfileIds = signal<Set<number>>(new Set<number>());
  readonly selectedAccountMarketplaces = computed(() =>
    [...this.selectedProfileIds()]
      .map((profileId) => this.byProfileId()?.get(profileId))
      .filter((am) => am !== undefined),
  );
  readonly canCreateNewAccountGroup = computed(() =>
    this.selectedAccountMarketplaces().every((am) =>
      this.selectedAccountMarketplaces().every((am2) => {
        if (am.profileId === am2.profileId) return true;

        return am.accountType === am2.accountType && am.marketplace !== am2.marketplace;
      }),
    ),
  );
  readonly canDeleteSelectedAccounts = computed(
    () =>
      this.canEditAccounts() && this.selectedAccountMarketplaces().every((am) => am.state !== AccountState.BIDDER_ON),
  );
  readonly selectedRowsCount = computed(() => this.selectedProfileIds().size);
  readonly textFilterConfig = new ITableTextFilterConfig<AccountMarketplaceEx>((o, filter) => {
    const toLowerCaseFilter = filter.toLowerCase();
    return (
      o.accountName.toLowerCase().includes(toLowerCaseFilter) || o.accountId.toLowerCase().includes(toLowerCaseFilter)
    );
  });
  protected paginationConfig = signal<ITablePagination>(new ITablePagination(this.DEFAULT_PAGE_SIZE));
  readonly eligibleTargetAccountGroups = computed(() => {
    const res: AccountGroup[] = [];
    const selectedAccountMarketplaces = [...this.selectedProfileIds()].map((profileId) =>
      this.byProfileId()?.get(profileId),
    );
    for (const accountGroup of this.organization()?.accountGroups ?? []) {
      if (selectedAccountMarketplaces.every((am) => am && this.isAccountGroupEligible(accountGroup, am.profileId!))) {
        res.push(accountGroup);
      }
    }
    return res;
  });

  readonly availableMarketplaces = computed(() =>
    Array.from(new Set(this.accountMarketplaces()?.map((am) => am.marketplace) ?? [])),
  );
  readonly availableAccountBidderStatuses = computed(() =>
    Array.from(
      new Set(this.accountMarketplaces()?.map((am) => this.accountSettingsV2Service.getAccountBidderStatus(am)) ?? []),
    ),
  );
  readonly availableAccountGroups = computed(() => this.organization()?.accountGroups ?? []);
  readonly availableFilters = computed<Filter<FilterCategory>[]>(() => [
    this.compactFilterService.getFilterWithOptions(
      FilterCategory.AccountBidderStatus,
      this.availableAccountBidderStatuses(),
    ),
    this.compactFilterService.getFilterWithOptions(FilterCategory.AccountGroup, this.availableAccountGroups()),
    this.compactFilterService.getFilterWithOptions(FilterCategory.Marketplace, this.availableMarketplaces()),
    this.compactFilterService.getFilterWithOptions(FilterCategory.AccountType, [
      AccountType.SELLER,
      AccountType.VENDOR,
    ]),
  ]);

  readonly isAllSelected = computed(() => {
    return this.dataSource.dataLength() > 0 && this.selectedProfileIds().size === this.dataSource.dataLength();
  });

  readonly dataSource = new ITableDataSource<AccountMarketplaceEx>(
    this.filteredAccountMarketplaces$,
    undefined,
    this.paginationConfig(),
    this.textFilterConfig,
  );

  readonly deleteSelectedAccountsLoading = signal(false);

  onFilterChange(filters: Filter<FilterCategory>[]) {
    this.filters$.next(filters);
  }

  onRowCheckboxClicked(profileId: number) {
    this.selectedProfileIds.update((selected) => {
      if (selected.has(profileId)) {
        selected.delete(profileId);
      } else {
        selected.add(profileId);
      }
      return new Set([...selected]);
    });
  }

  clearSelection() {
    this.selectedProfileIds.set(new Set<number>());
  }

  isAccountGroupEligible(accountGroup: AccountGroup, profileId: number) {
    const am = this.byProfileId()?.get(profileId);
    return accountGroup.resources.every(
      (resource) => resource.accountType === am?.accountType && resource.marketplace !== am?.marketplace,
    );
  }

  moveToNewAccountGroup() {
    if (!this.canEditAccounts() || !this.canCreateNewAccountGroup()) return;
    const ref = this.modalService.openModal<ModalCreateAccountGroupData, boolean>(ModalCreateAccountGroupComponent, {
      size: "sm",
      modalTitle: this.translocoService.translate("new-account-group-popup.account_group_creation"),
      data: {
        selectedAccounts: this.selectedAccountMarketplaces(),
      },
    });
    ref.afterClosed().subscribe((result) => {
      if (result) {
        this.clearSelection();
      }
    });
  }

  moveSelectedAccountsToGroup(accountGroup: AccountGroup) {
    if (!this.canEditAccounts()) return;
    return this.accountSettingsV2Service
      .moveMultipleAccountsToGroup(this.selectedAccountMarketplaces(), accountGroup)
      .subscribe({
        next: () => {
          this.toasterService.success("Successfully moved the accounts to " + accountGroup.name, "Accounts moved");
          this.clearSelection();
        },
        error: (error: string) => {
          this.toasterService.error(error, "Accounts move error");
        },
      });
  }

  deleteSelectedAccounts() {
    if (!this.canDeleteSelectedAccounts()) {
      return;
    }
    const ref = this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("account-setting.remove_accounts"),
      data: {
        message: this.translocoService.translate("account-setting.remove_accounts_message"),
        color: "red",
        confirmLabel: this.translocoService.translate("account-setting.remove_accounts_confirm_label"),
      },
    });
    ref
      .afterClosed()
      .pipe(
        tap(() => this.deleteSelectedAccountsLoading.set(true)),
        switchMap(() => this.accountSettingsV2Service.deleteMultipleAccounts(this.selectedAccountMarketplaces())),
      )
      .subscribe({
        next: () => {
          this.toasterService.success("Accounts deleted", "Accounts deleted");
          this.clearSelection();
        },
        error: (error: string) => {
          this.toasterService.error(error, "Accounts delete error");
        },
        complete: () => {
          this.deleteSelectedAccountsLoading.set(false);
        },
      });
  }
  openEditDefaultCampaignLabelModal() {
    if (!this.organization()) return;
    this.accountSettingsV2Service.openEditDefaultCampaignLabelModal(this.organization()!);
  }

  toggleSelectAllProducts() {
    this.selectedProfileIds.update((selected) => {
      if (this.isAllSelected()) {
        return new Set<number>();
      }
      return new Set([...selected, ...Array.from(this.dataSource.filteredData()?.map((am) => am.profileId!) ?? [])]);
    });
  }
}
